import { useState, useEffect, useCallback } from 'react';
import {
    Layers, Plus, Pencil, Trash2, RefreshCw,
    X, AlertCircle, Minus
} from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import { formatTL } from '../../utils/money';
import './StackingFeePage.css';

const DiscountType = { Percentage: 0, FixedAmount: 1 };
const DiscountTypeLabels = { 0: 'Yüzdesel', 1: 'Sabit Tutar' };

const DEFAULT_TIER = { orderIndex: 1, discountType: DiscountType.Percentage, discountValue: 100 };

const EMPTY_FORM = {
    name: '',
    validFrom: new Date().toISOString().slice(0, 16),
    validTo: '',
    isActive: true,
    notes: '',
    tiers: [{ ...DEFAULT_TIER }],
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
    });
};

const formatTierValue = (tier) => {
    if (tier.discountType === DiscountType.Percentage) {
        return `%${tier.discountValue}`;
    }
    return formatTL(tier.discountValue);
};

const StackingFeePage = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiService.get(API_ENDPOINTS.STACKING_FEE_RULE.LIST);
            if (res.success) {
                setRecords(res.data || []);
            }
        } catch {
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    const openAdd = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditingId(record.id);
        const tiers = (record.tiers || [])
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map(t => ({
                orderIndex: t.orderIndex,
                discountType: t.discountType ?? DiscountType.Percentage,
                discountValue: t.discountValue ?? 0,
            }));
        setForm({
            name: record.name || '',
            validFrom: record.validFrom ? new Date(record.validFrom).toISOString().slice(0, 16) : '',
            validTo: record.validTo ? new Date(record.validTo).toISOString().slice(0, 16) : '',
            isActive: record.isActive,
            notes: record.notes || '',
            tiers: tiers.length > 0 ? tiers : [{ ...DEFAULT_TIER }],
        });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleTierChange = (index, field, value) => {
        setForm(prev => {
            const tiers = [...prev.tiers];
            tiers[index] = { ...tiers[index], [field]: value };
            return { ...prev, tiers };
        });
    };

    const addTier = () => {
        setForm(prev => {
            const nextIndex = prev.tiers.length + 1;
            return {
                ...prev,
                tiers: [...prev.tiers, { orderIndex: nextIndex, discountType: DiscountType.Percentage, discountValue: 0 }],
            };
        });
    };

    const removeTier = (index) => {
        setForm(prev => {
            const tiers = prev.tiers.filter((_, i) => i !== index).map((t, i) => ({ ...t, orderIndex: i + 1 }));
            return { ...prev, tiers };
        });
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.validFrom) {
            fleetToast.error('Zorunlu alanları doldurun');
            return;
        }
        if (form.tiers.length === 0) {
            fleetToast.error('En az bir kademe ekleyin');
            return;
        }
        for (const tier of form.tiers) {
            if (tier.discountValue === '' || tier.discountValue == null || isNaN(tier.discountValue)) {
                fleetToast.error('Tüm kademe değerlerini doldurun');
                return;
            }
        }

        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                validFrom: new Date(form.validFrom).toISOString(),
                validTo: form.validTo ? new Date(form.validTo).toISOString() : null,
                isActive: form.isActive,
                notes: form.notes || null,
                tiers: form.tiers.map(t => ({
                    orderIndex: t.orderIndex,
                    discountType: t.discountType,
                    discountValue: parseFloat(t.discountValue),
                })),
            };

            let res;
            if (editingId) {
                res = await apiService.put(API_ENDPOINTS.STACKING_FEE_RULE.UPDATE(editingId), payload);
            } else {
                res = await apiService.post(API_ENDPOINTS.STACKING_FEE_RULE.CREATE, payload);
            }

            if (res.success) {
                fleetToast.success(editingId ? 'Kural güncellendi' : 'Kural eklendi');
                closeModal();
                fetchRecords();
            } else {
                fleetToast.error(res.error || 'İşlem başarısız');
            }
        } catch {
            fleetToast.error('Bir hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            const res = await apiService.delete(API_ENDPOINTS.STACKING_FEE_RULE.DELETE(deleteId));
            if (res.success) {
                fleetToast.success('Kural silindi');
                setDeleteId(null);
                fetchRecords();
            } else {
                fleetToast.error(res.error || 'Silme başarısız');
            }
        } catch {
            fleetToast.error('Bir hata oluştu');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="cc-page">
            <div className="page-header">
                <div className="header-left">
                    <div className="page-icon"><Layers size={20} /></div>
                    <div className="page-title">
                        <h1>Yığın İstifleme Kuralları</h1>
                        <p>Aynı restorandan çoklu paket teslimlerinde uygulanan kademe indirimlerini yönetin</p>
                    </div>
                </div>
                <div className="header-right">
                    <button className="btn btn-secondary" onClick={fetchRecords}>
                        <RefreshCw size={16} />
                    </button>
                    <button className="btn btn-primary" onClick={openAdd}>
                        <Plus size={16} /> Yeni Kural
                    </button>
                </div>
            </div>

            <div className="sf-table-section">
                <div className="sf-table-header">
                    <h2>Kural Listesi</h2>
                    <span className="sf-record-count">{records.length} kural</span>
                </div>

                {loading ? (
                    <div className="page-loading"><div className="spinning sf-spinner-circle"></div></div>
                ) : records.length === 0 ? (
                    <div className="page-empty">
                        <Layers size={40} />
                        <p>Henüz yığın istifleme kuralı bulunmuyor</p>
                        <button className="btn btn-primary" onClick={openAdd}>
                            <Plus size={16} /> İlk Kuralı Ekle
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="data-table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Kural Adı</th>
                                        <th>Kademeler</th>
                                        <th>Geçerlilik</th>
                                        <th>Durum</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((r) => (
                                        <tr key={r.id} className={r.isActive ? 'sf-row-active' : ''}>
                                            <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.name || '-'}</td>
                                            <td>
                                                <div className="sf-tier-badges">
                                                    {(r.tiers || []).sort((a, b) => a.orderIndex - b.orderIndex).map((t, i) => (
                                                        <span key={i} className="sf-tier-badge">
                                                            <span className="sf-tier-num">{t.orderIndex}.</span>
                                                            {formatTierValue(t)}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>
                                                <div>{formatDate(r.validFrom)}</div>
                                                {r.validTo && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Bitiş: {formatDate(r.validTo)}</div>}
                                            </td>
                                            <td>
                                                <span className={`sf-status ${r.isActive ? 'active' : 'passive'}`}>
                                                    {r.isActive ? 'Aktif' : 'Pasif'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="sf-actions">
                                                    <button className="action-btn edit" onClick={() => openEdit(r)} title="Düzenle">
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button className="action-btn delete" onClick={() => setDeleteId(r.id)} title="Sil">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="sf-cards-mobile">
                            {records.map((r) => (
                                <div key={r.id} className={`sf-card ${r.isActive ? 'sf-card-active' : ''}`}>
                                    <div className="sf-card-top">
                                        <span className="sf-card-name">{r.name || '-'}</span>
                                        <div className="sf-card-top-right">
                                            <span className={`sf-status ${r.isActive ? 'active' : 'passive'}`}>
                                                {r.isActive ? 'Aktif' : 'Pasif'}
                                            </span>
                                            <div className="sf-actions">
                                                <button className="action-btn edit" onClick={() => openEdit(r)}><Pencil size={14} /></button>
                                                <button className="action-btn delete" onClick={() => setDeleteId(r.id)}><Trash2 size={14} /></button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="sf-card-grid">
                                        <div><span>Başlangıç</span><strong>{formatDate(r.validFrom)}</strong></div>
                                        {r.validTo && <div><span>Bitiş</span><strong>{formatDate(r.validTo)}</strong></div>}
                                    </div>
                                    <div className="sf-card-tiers">
                                        {(r.tiers || []).sort((a, b) => a.orderIndex - b.orderIndex).map((t, i) => (
                                            <span key={i} className="sf-tier-badge">
                                                <span className="sf-tier-num">{t.orderIndex}.</span>
                                                {formatTierValue(t)}
                                            </span>
                                        ))}
                                    </div>
                                    {r.notes && <div className="sf-card-notes">{r.notes}</div>}
                                    <div className="sf-card-footer">{formatDate(r.validFrom)}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {modalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingId ? 'Kural Düzenle' : 'Yeni Kural'}</h2>
                            <button className="modal-close" onClick={closeModal}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Kural Adı *</label>
                                <input type="text" value={form.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    placeholder="Örn: Standart Yığın İstifleme" />
                            </div>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Geçerlilik Başlangıcı *</label>
                                    <input type="datetime-local" value={form.validFrom}
                                        onChange={(e) => handleChange('validFrom', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label>Geçerlilik Bitişi</label>
                                    <input type="datetime-local" value={form.validTo}
                                        onChange={(e) => handleChange('validTo', e.target.value)} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Not</label>
                                <textarea rows={3} maxLength={1000} value={form.notes}
                                    onChange={(e) => handleChange('notes', e.target.value)} placeholder="Opsiyonel not..." />
                            </div>
                            <div className="form-group sf-form-check">
                                <label>
                                    <input type="checkbox" checked={form.isActive}
                                        onChange={(e) => handleChange('isActive', e.target.checked)} />
                                    <span>Aktif</span>
                                </label>
                            </div>

                            <div className="sf-tiers-section">
                                <div className="sf-tiers-label">
                                    <span>Kademeler</span>
                                </div>
                                {form.tiers.map((tier, idx) => (
                                    <div key={idx} className="sf-tier-row">
                                        <span className="sf-tier-index">{tier.orderIndex}. Paket</span>
                                        <div className="sf-tier-type-toggle">
                                            <button
                                                type="button"
                                                className={`sf-tier-type-btn ${tier.discountType === DiscountType.Percentage ? 'selected' : ''}`}
                                                onClick={() => handleTierChange(idx, 'discountType', DiscountType.Percentage)}
                                            >
                                                Yüzdesel
                                            </button>
                                            <button
                                                type="button"
                                                className={`sf-tier-type-btn ${tier.discountType === DiscountType.FixedAmount ? 'selected' : ''}`}
                                                onClick={() => handleTierChange(idx, 'discountType', DiscountType.FixedAmount)}
                                            >
                                                Sabit Tutar
                                            </button>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            className="sf-tier-value-input"
                                            value={tier.discountValue}
                                            onChange={(e) => handleTierChange(idx, 'discountValue', e.target.value)}
                                        />
                                        <span className="sf-tier-suffix">
                                            {tier.discountType === DiscountType.Percentage ? '%' : 'TL'}
                                        </span>
                                        {idx > 0 && (
                                            <button type="button" className="sf-tier-remove" onClick={() => removeTier(idx)} title="Kademeyi Sil">
                                                <Minus size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button type="button" className="sf-add-tier-btn" onClick={addTier}>
                                    <Plus size={14} /> Kademe Ekle
                                </button>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>İptal</button>
                            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? 'Kaydediliyor...' : (editingId ? 'Güncelle' : 'Kaydet')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteId && (
                <div className="modal-overlay" onClick={() => setDeleteId(null)}>
                    <div className="modal sf-modal-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Kural Sil</h2>
                            <button className="modal-close" onClick={() => setDeleteId(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="sf-delete-warn">
                                <AlertCircle size={40} />
                                <p>Bu yığın istifleme kuralını silmek istediğinize emin misiniz?</p>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={() => setDeleteId(null)} disabled={deleting}>İptal</button>
                            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                                {deleting ? 'Siliniyor...' : 'Sil'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StackingFeePage;
